import { roundTo, roundToUp } from "round-to";
import { formatNumber, formatNumberToMM } from "../../../utils/number";
import { months } from "../../../utils/helper";
import { construirGraficoPorHora } from "../../../utils/hourly";

const SEM_FORMA_PAGAMENTO = 'Não informado';
const SEM_USUARIO = 'Não informado';
const SEM_UNIDADE = 'Não informado';

const FORMATO_MOEDA = { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 };

// Log antigo de exclusão pode não ter o valor do pagamento no JSON: entra como
// null e precisa somar zero, nunca NaN (um NaN contamina o card inteiro).
function toNumber(value) {
    const parsed = parseFloat(value);

    return Number.isFinite(parsed) ? parsed : 0;
}

function sumValue(data) {
    return (data || []).reduce((sum, exclusion) => sum + toNumber(exclusion?.valor), 0);
}

function sumValueToDate(data, targetDate) {
    return (data || []).reduce((sum, exclusion) => {
        if (exclusion?.dia <= targetDate) {
            return sum + toNumber(exclusion?.valor);
        }

        return sum;
    }, 0);
}

function countToDate(data, targetDate) {
    return (data || []).filter((exclusion) => exclusion?.dia <= targetDate).length;
}

// Divisão por zero vira 0 em vez de Infinity/NaN — o card mostra o número cru.
function variacao(atual, anterior) {
    if (!anterior) {
        return 0;
    }

    return roundToUp(((atual - anterior) / anterior) * 100, 2);
}

function proporcao(parte, total) {
    if (!total) {
        return 0;
    }

    return roundTo((parte / total) * 100, 2);
}

function formatCurrency(value, isMobile) {
    return isMobile ? formatNumberToMM(value) : formatNumber(value, FORMATO_MOEDA);
}

function groupBy(data, getKey) {
    const groupedData = {};

    (data || []).forEach((item) => {
        const key = getKey(item);

        if (!groupedData[key]) {
            groupedData[key] = { description: key, records: [] };
        }

        groupedData[key].records.push(item);
    });

    return Object.values(groupedData);
}

const nomeUnidade = (item) => item?.unidadeName || SEM_UNIDADE;
const nomeUsuario = (item) => item?.nomeUsuario || item?.usernameUsuario || SEM_USUARIO;
const nomeFormaPagamento = (item) => item?.formaPagamento || SEM_FORMA_PAGAMENTO;

export const PaymentExclusionCountKPI = (paymentExclusion, isMobile) => {
    const {
        currentMonthPaymentExclusion,
        lastMonthPaymentExclusion,
        currentMonth,
        currentDay,
        isCurrentMonth,
    } = paymentExclusion || {};

    const existsCurrentMonth = currentMonthPaymentExclusion && currentMonthPaymentExclusion.length;
    const existsLastMonth = lastMonthPaymentExclusion && lastMonthPaymentExclusion.length;

    if (!existsCurrentMonth) {
        return {
            caption: 'Qtde. pagamentos excluídos',
            descriptionComparative: '',
            metric: 0,
            badgeDelta: 0,
            progress: 0,
            target: 0,
            deltaType: 'moderateIncrease',
        };
    }

    const actualMonth = currentMonth - 1;
    const lastMonth = (actualMonth - 1 + 12) % 12;

    const countCurrent = currentMonthPaymentExclusion.length;

    const result = {
        caption: 'Qtde. pagamentos excluídos',
        descriptionComparative: `${months[lastMonth]}`,
        metric: isMobile ? formatNumberToMM(countCurrent) : formatNumber(countCurrent, { maximumFractionDigits: 0 }),
    };

    if (existsLastMonth) {
        const countLast = lastMonthPaymentExclusion.length;

        result.target = isMobile ? formatNumberToMM(countLast) : formatNumber(countLast, { maximumFractionDigits: 0 });
        result.progress = proporcao(countCurrent, countLast);
        result.badgeDelta = isCurrentMonth
            ? variacao(countCurrent, countToDate(lastMonthPaymentExclusion, currentDay))
            : variacao(countCurrent, countLast);
    }

    return result;
};

export const PaymentExclusionValueKPI = (paymentExclusion, isMobile) => {
    const {
        currentMonthPaymentExclusion,
        lastMonthPaymentExclusion,
        currentMonth,
        currentDay,
        isCurrentMonth,
    } = paymentExclusion || {};

    const existsCurrentMonth = currentMonthPaymentExclusion && currentMonthPaymentExclusion.length;
    const existsLastMonth = lastMonthPaymentExclusion && lastMonthPaymentExclusion.length;

    if (!existsCurrentMonth) {
        return {
            caption: 'R$ Pagamentos excluídos',
            descriptionComparative: '',
            metric: 0,
            badgeDelta: 0,
            progress: 0,
            target: 0,
            deltaType: 'moderateIncrease',
        };
    }

    const actualMonth = currentMonth - 1;
    const lastMonth = (actualMonth - 1 + 12) % 12;

    const sumCurrent = sumValue(currentMonthPaymentExclusion);

    const result = {
        caption: 'R$ Pagamentos excluídos',
        descriptionComparative: `${months[lastMonth]}`,
        metric: formatCurrency(sumCurrent, isMobile),
    };

    if (existsLastMonth) {
        const sumLast = sumValue(lastMonthPaymentExclusion);

        result.target = formatCurrency(sumLast, isMobile);
        result.progress = proporcao(sumCurrent, sumLast);
        result.badgeDelta = isCurrentMonth
            ? variacao(sumCurrent, sumValueToDate(lastMonthPaymentExclusion, currentDay))
            : variacao(sumCurrent, sumLast);
    }

    return result;
};

// Card acumulado genérico: agrupa por uma dimensão (unidade / forma de pagamento) e
// abre o detalhamento por outra, no molde dos cards de Itens cancelados.
function cumulativeKPI(paymentExclusion, { caption, agrupamento, detalhamento }) {
    const {
        currentMonthPaymentExclusion,
        lastMonthPaymentExclusion,
        currentMonth,
    } = paymentExclusion || {};

    const result = {
        height: 'h-[800px]',
        caption,
        data: [],
        yAxisWidth: 180,
        index: "description",
        categories: [],
        layout: 'vertical',
    };

    if (!currentMonthPaymentExclusion || !currentMonthPaymentExclusion.length) {
        return result;
    }

    const actualMonth = currentMonth - 1;
    const lastMonth = (actualMonth - 1 + 12) % 12;

    const currentMonthDescription = `${months[actualMonth]}`;
    const lastMonthDescription = `${months[lastMonth]}`;

    result.categories = [lastMonthDescription, currentMonthDescription];

    const groupedCurrentMonth = groupBy(currentMonthPaymentExclusion, agrupamento);
    const groupedLastMonth = groupBy(lastMonthPaymentExclusion, agrupamento);

    const totalCurrentMonth = sumValue(currentMonthPaymentExclusion);
    const totalLastMonth = sumValue(lastMonthPaymentExclusion);

    const descriptions = [...new Set([...groupedCurrentMonth, ...groupedLastMonth].map(item => item.description))];

    function findByDescription(data, description) {
        return data.find(item => item.description === description);
    }

    function cumulativeList(data, description) {
        const totals = {};

        (findByDescription(data, description)?.records || []).forEach((record) => {
            const key = detalhamento(record);
            totals[key] = (totals[key] || 0) + toNumber(record?.valor);
        });

        return Object.entries(totals)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }

    result.data = descriptions.map((description) => {
        const metricLastMonth = sumValue(findByDescription(groupedLastMonth, description)?.records);
        const metricCurrentMonth = sumValue(findByDescription(groupedCurrentMonth, description)?.records);

        return {
            description,
            [lastMonthDescription]: {
                metric: metricLastMonth,
                delta: proporcao(metricLastMonth, totalLastMonth),
                cumulativeList: cumulativeList(groupedLastMonth, description),
            },
            [currentMonthDescription]: {
                metric: metricCurrentMonth,
                delta: proporcao(metricCurrentMonth, totalCurrentMonth),
                cumulativeList: cumulativeList(groupedCurrentMonth, description),
            },
        };
    });

    return result;
}

export const PaymentExclusionPerHourKPI = (paymentExclusion) => construirGraficoPorHora({
    caption: 'Exclusões de pagamento por hora',
    currentMonth: paymentExclusion?.currentMonth,
    registrosMesAtual: paymentExclusion?.currentMonthPaymentExclusion,
    registrosMesAnterior: paymentExclusion?.lastMonthPaymentExclusion,
});

export const CumulativePaymentExclusionByUserKPI = (paymentExclusion) => cumulativeKPI(paymentExclusion, {
    caption: 'Exclusões de pagamento (Unidade X Usuário)',
    agrupamento: nomeUnidade,
    detalhamento: nomeUsuario,
});

export const CumulativePaymentExclusionByPaymentTypeKPI = (paymentExclusion) => cumulativeKPI(paymentExclusion, {
    caption: 'Exclusões de pagamento (Forma de pagamento X Unidade)',
    agrupamento: nomeFormaPagamento,
    detalhamento: nomeUnidade,
});
